import { useDebouncedCallback, usePrevious } from "@react-hookz/web";
import { MutableRefObject, RefCallback, RefObject, useCallback, useEffect, useRef } from "react";

export interface UseResizeObserverOptions {
    /**
     * The max time (in ms) the debounce code will wait before firing the resize function.
     */
    debounceMaxMs?: number;

    /**
     * If specified, will debounce the onResize handler.
     */
    debounceMs?: number;

    /**
     * Defaults to true. If false, the ResizeObserver will be disconnected.
     */
    enabled?: boolean;

    /**
     * An optional ref that will be forked within this component.
     * Please apply the returned ref to the component being measured, NOT this ref.
     */
    targetRef?: MutableRefObject<HTMLElement> | RefObject<HTMLElement>;
}

export interface UseResizeObserverResult {
    /**
     * Attach this ref to the element you want to observe.
     */
    ref: RefCallback<HTMLElement>;
}

/**
 * Wraps common functionality up around ResizeObservers.
 */
export function useResizeObserver<ElementType extends HTMLElement>(
    onResize: ResizeObserverCallback,
    { debounceMaxMs = undefined, debounceMs = 0, enabled = true }: UseResizeObserverOptions = {}
) {
    const observerRef = useRef<ResizeObserver | null>(null);
    const observedElement = useRef<ElementType | null>(null);
    const debounceEnabled = debounceMs > 0;
    const debouncedOnResize = useDebouncedCallback(onResize, [onResize], debounceMs, debounceMaxMs);
    const callback = debounceEnabled ? debouncedOnResize : onResize;

    const fnRef = useCallback(
        (element: ElementType | null) => {
            observedElement.current = element;
            if (observerRef.current) {
                // If we already had a ResizeObserver, clean up and replace it.
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            if (window.ResizeObserver && element && enabled) {
                observerRef.current = new ResizeObserver((...args) => {
                    callback(...args);
                });
                observerRef.current.observe(element);
            }
        },
        [callback, enabled]
    );

    // If enabled changes, turn on/off the ResizeObserver.
    const previousEnabled = usePrevious(enabled);
    useEffect(() => {
        if (observerRef.current && observedElement.current) {
            if (!previousEnabled && enabled) {
                observerRef.current.observe(observedElement.current);
            } else if (previousEnabled && !enabled) {
                observerRef.current.unobserve(observedElement.current);
            }
        }
    }, [enabled, previousEnabled]);

    // Turn off the observer when we unmount.
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    return {
        ref: fnRef,
    };
}
